import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  AccessTime,
  People,
  Settings,
  Receipt,
  ArrowBack,
  Edit as EditIcon,
  LocationOn,
  Phone,
  Email
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import PageContainer from '../shared/PageContainer';
import BranchUserManagement from './BranchUserManagement';
import EditBranchDialog from './EditBranchDialog';

const BranchDetails = () => {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const fetchBranchDetails = useCallback(async () => {
    try {
      const response = await api.get(`/branches/${branchId}`);
      setBranch(response.data.branch);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load branch details');
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    fetchBranchDetails();
  }, [branchId, fetchBranchDetails]);

  const handleEditSuccess = (updatedBranch) => {
    setBranch(updatedBranch);
  };

  if (loading) {
    return (
      <PageContainer title="Branch Details">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer title="Branch Details">
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/app/branches')}>
              Back to Branches
            </Button>
          }
        >
          {error}
        </Alert>
      </PageContainer>
    );
  }

  if (!branch) {
    return (
      <PageContainer title="Branch Details">
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/app/branches')}>
              Back to Branches
            </Button>
          }
        >
          Branch not found
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={branch.name}
      subtitle={`Branch Code: ${branch.branch_code}`}
      actions={
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/app/branches')}
          >
            Back
          </Button>
          {user?.role === 'admin' && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditDialogOpen(true)}
            >
              Edit Branch
            </Button>
          )}
        </>
      }
    >
      {/* Branch Info Summary */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{
          display: 'flex',
          gap: { xs: 2, md: 4 },
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          <Chip
            label={branch.status === 'active' ? 'Active' : 'Inactive'}
            color={branch.status === 'active' ? 'success' : 'default'}
            size="small"
            variant="outlined"
          />
          {branch.is_main_branch && (
            <Chip label="Main Branch" color="primary" size="small" variant="outlined" />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <LocationOn sx={{ fontSize: 18, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">{branch.address}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Phone sx={{ fontSize: 18, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">{branch.phone}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Email sx={{ fontSize: 18, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">{branch.email}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <People sx={{ fontSize: 18, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              {branch.user_count || 0} {(branch.user_count || 0) === 1 ? 'User' : 'Users'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500
            }
          }}
        >
          <Tab
            icon={<AccessTime sx={{ fontSize: 20 }} />}
            label="Operating Hours"
            iconPosition="start"
          />
          <Tab
            icon={<People sx={{ fontSize: 20 }} />}
            label="Users"
            iconPosition="start"
          />
          <Tab
            icon={<Receipt sx={{ fontSize: 20 }} />}
            label="Tax Settings"
            iconPosition="start"
          />
          <Tab
            icon={<Settings sx={{ fontSize: 20 }} />}
            label="Settings"
            iconPosition="start"
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && <OperatingHours hours={branch.settings?.operating_hours} />}
          {activeTab === 1 && <BranchUserManagement branchId={branchId} branchName={branch.name} />}
          {activeTab === 2 && <TaxSettings settings={branch.settings?.tax_settings} />}
          {activeTab === 3 && <BranchSettings branch={branch} onUpdate={fetchBranchDetails} />}
        </Box>
      </Paper>

      {/* Edit Dialog */}
      {editDialogOpen && (
        <EditBranchDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          branch={branch}
          onSuccess={handleEditSuccess}
        />
      )}
    </PageContainer>
  );
};

const OperatingHours = ({ hours = {} }) => {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <Grid container spacing={3}>
      {days.map(day => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={day}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              bgcolor: day === 'sunday' ? 'grey.50' : 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                textTransform: 'capitalize',
                fontWeight: 600,
                color: day === 'sunday' ? 'text.secondary' : 'text.primary'
              }}
            >
              {day}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <AccessTime sx={{ color: 'action.active', fontSize: '1.2rem' }} />
              <Typography color={day === 'sunday' ? 'text.secondary' : 'text.primary'}>
                {hours[day]?.open ? `${hours[day].open} - ${hours[day].close}` : 'Closed'}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

const TaxSettings = ({ settings = {} }) => {
  const defaultSettings = {
    default_tax_rate: 0,
    tax_inclusive: false
  };

  const currentSettings = { ...defaultSettings, ...settings };

  return (
    <Card elevation={0}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Tax Configuration
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Default Tax Rate (%)"
              type="number"
              value={currentSettings.default_tax_rate}
              InputProps={{
                readOnly: true
              }}
            />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={currentSettings.tax_inclusive}
                  disabled
                />
              }
              label="Prices are tax inclusive"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const BranchSettings = ({ branch, onUpdate }) => {
  return (
    <Card elevation={0}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Branch Information
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Branch Name"
              value={branch.name}
              InputProps={{
                readOnly: true
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Branch Code"
              value={branch.branch_code}
              InputProps={{
                readOnly: true
              }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Address"
              value={branch.address}
              InputProps={{
                readOnly: true
              }}
              multiline
              rows={2}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Phone"
              value={branch.phone}
              InputProps={{
                readOnly: true
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Email"
              value={branch.email}
              InputProps={{
                readOnly: true
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default BranchDetails;
